import { forwardRef, useCallback, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

const TriggerInput = forwardRef(({ value, onClick, onPosition, placeholder, testId }, ref) => (
  <button
    type="button"
    ref={ref}
    onClick={(event) => {
      onPosition?.(event.currentTarget);
      onClick?.(event);
    }}
    className="w-full flex items-center justify-between gap-2 rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm text-left focus:outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 transition"
    data-testid={testId}
  >
    <span className={value ? "text-neutral-900" : "text-neutral-400"}>{value || placeholder}</span>
    <CalendarIcon size={16} className="text-brand-orange shrink-0" />
  </button>
));
TriggerInput.displayName = "TriggerInput";

function CalendarHeader({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) {
  return (
    <div className="suk-datepicker-header">
      <button
        type="button"
        className="suk-datepicker-header__nav"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        aria-label="Previous month"
      >
        <span aria-hidden="true" className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous" />
      </button>
      <div className="suk-datepicker-header__title">{format(date, "MMMM yyyy")}</div>
      <button
        type="button"
        className="suk-datepicker-header__nav"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        aria-label="Next month"
      >
        <span aria-hidden="true" className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next" />
      </button>
    </div>
  );
}

// Professional date picker (react-datepicker) — date only, future only, rendered in a full-screen portal
// so it always appears above modals/headers/overlays.
export function DateField({ value, onChange, placeholder = "Select Date", testId = "date-field" }) {
  const triggerRef = useRef(null);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const selected = value ? new Date(value + "T00:00:00") : null;
  const positionCalendar = useCallback((trigger = triggerRef.current) => {
    if (!trigger) return;
    triggerRef.current = trigger;
    const rect = trigger.getBoundingClientRect();
    const calendarWidth = Math.min(290, window.innerWidth - 16);
    const calendarHeight = 282;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - calendarWidth - 8));
    const top = rect.bottom + 8 + calendarHeight <= window.innerHeight - 8
      ? rect.bottom + 8
      : Math.max(8, rect.top - calendarHeight - 8);
    document.documentElement.style.setProperty("--suk-datepicker-left", `${left}px`);
    document.documentElement.style.setProperty("--suk-datepicker-top", `${top}px`);
  }, []);

  useEffect(() => {
    const updatePosition = () => positionCalendar();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [positionCalendar]);

  return (
    <DatePicker
      selected={selected}
      onChange={(d) => onChange(d ? format(d, "yyyy-MM-dd") : "")}
      minDate={today}
      dateFormat="EEE, d MMM yyyy"
      placeholderText={placeholder}
      portalId="suk-datepicker-portal"
      wrapperClassName="w-full"
      popperClassName="suk-datepicker-popper"
      popperPlacement="bottom-start"
      popperProps={{ strategy: "fixed" }}
      popperModifiers={[
        { name: "offset", options: { offset: [0, 8] } },
        { name: "flip", options: { fallbackPlacements: ["top-start", "bottom-end", "top-end"] } },
        { name: "preventOverflow", options: { padding: 8 } },
      ]}
      shouldCloseOnSelect
      closeOnScroll={false}
      onCalendarOpen={() => requestAnimationFrame(() => positionCalendar())}
      renderCustomHeader={(headerProps) => <CalendarHeader {...headerProps} />}
      showPopperArrow={false}
      customInput={<TriggerInput placeholder={placeholder} testId={testId} onPosition={positionCalendar} />}
    />
  );
}
